import { computed } from 'vue';
import { usePermissionsStore } from 'stores/permissions-store';
import { useFetchFeatureFlagsQuery } from 'src/queries/feature-flags/useFetchFeatureFlagsQuery';
import {
  FeatureFlagScope,
  isEnabled,
  isEnabledAnywhere,
  narrowIdsByFlagScope,
} from 'src/utils/featureFlags';

const FEATURE_NAME = 'annual-returns';

/**
 * The permissions that grant administration of returns at each scope. The
 * bundle records the specific ids each resolves to under `allow.regions` and
 * `allow.units`.
 */
const MANAGE_REGION_PERMISSION = 'ANNUAL_RETURNS:ALLOW:MANAGE_REGION';
const MANAGE_UNIT_PERMISSION = 'ANNUAL_RETURNS:ALLOW:MANAGE_UNIT';

/**
 * Resolves what the annual returns interface may offer the current user, by
 * combining the two mechanisms that each answer half the question.
 *
 * The ACL says what the user is entitled to administer. The `annual-returns`
 * feature flag says how much of that entitlement is actually live on the Portal
 * rather than still being completed in Adelphi. The interface may only offer the
 * intersection, so this composable is the single place that intersection is
 * taken — callers read the narrowed lists rather than the raw permission
 * bundle.
 *
 * Flag scopes carry a wildcard where the API has deliberately declined to
 * enumerate an axis (every unit, for an administrator), which leaves the ACL as
 * the only filter on it.
 *
 * Visibility only. Authorisation is unchanged and still enforced per request by
 * the API.
 */
export function useAnnualReturnsVisibility() {
  const permissionsStore = usePermissionsStore();
  const {
    data: featureFlags,
    isLoading,
    isPending,
  } = useFetchFeatureFlagsQuery();

  const flag = computed(() => featureFlags.value?.[FEATURE_NAME]);

  /**
   * Whether the user holds the feature at all. This is the question the
   * navigation and the route guard ask; it deliberately ignores scope, because
   * the API omits the flag entirely for a user it refuses.
   */
  const isFeatureEnabled = computed(
    () =>
      !isPending.value &&
      !!featureFlags.value &&
      isEnabledAnywhere(FEATURE_NAME, featureFlags.value),
  );

  /** Regions the user administers that have at least one unit live on the Portal. */
  const visibleRegionIds = computed(() =>
    narrowIdsByFlagScope(
      permissionsStore.getAllowedRegionIds(MANAGE_REGION_PERMISSION),
      flag.value?.regions,
    ),
  );

  /** Units the user administers that are live on the Portal. */
  const visibleUnitIds = computed(() =>
    narrowIdsByFlagScope(
      permissionsStore.getAllowedUnitIds(MANAGE_UNIT_PERMISSION),
      flag.value?.units,
    ),
  );

  /**
   * Whether one specific region or unit may be offered. Pass a unit's
   * `regionId` alongside its `unitId` so a wildcard unit list can be resolved
   * against the region the unit sits in.
   */
  function canSee(scope: FeatureFlagScope): boolean {
    if (!featureFlags.value) return false;
    return isEnabled(FEATURE_NAME, featureFlags.value, scope);
  }

  return {
    isLoading,
    isPending,
    isFeatureEnabled,
    visibleRegionIds,
    visibleUnitIds,
    canSee,
  };
}
